import { action, observable, makeObservable } from 'mobx';
import { GameInfo } from '../games/game-info';
import { OnGameCloseParams, OnGameOpenParams } from '../sdk/sdk-events';
import { ServiceBase } from '../ServiceBase';
import { IRunningGameService } from './running-game.service.interface';
import {IServiceFactoryExtended} from "../service-factory-extended.interface";

export class RunningGameService extends ServiceBase implements IRunningGameService {
  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);
  }

  get isGameRunning(): boolean {
    return this.services.sdk.isGameRunning();
  }

  continuePlayingCurrentGame(): void {
    this.services.sdk.continuePlayingCurrentGame();
  }

  stopCurrentGameAutoplay(): void {
    this.services.sdk.stopCurrentGameAutoplay();
  }

  closeRunningGame(): void {
    this.services.gameRunner.currentRunningGame?.closeGame({
      shouldSuggestNextGame: false
    });
  }

  @observable.ref
  lastPlayedRealGame: GameInfo = null;

  @observable.ref
  correlatedGamesForLastPlayedRealGame: GameInfo[] = [];

  private _listenForLastRealGame = false;


  @action.bound
  _setLastPlayedRealGame(lastGameInfo: GameInfo) {
    this.lastPlayedRealGame = lastGameInfo;
  }

  @action.bound
  _setCorrelatedGamesForLastPlayedRealGame(gameInfos: GameInfo[]) {
    this.correlatedGamesForLastPlayedRealGame = gameInfos;
  }

  listenForLastPlayedRealGame() {
    if (this._listenForLastRealGame) {
      return;
    }

    this.services.sdk.events.onGameClose((closeOptions: OnGameCloseParams) => {
      // showNextGameSuggestion should be filled as true when player itself closes the game (press on X button)
      if (closeOptions.isReal && closeOptions.isGamePlayed && closeOptions.showNextGameSuggestion) {
        this.setLastPlayedRealGame(closeOptions.gameType);
      }
    });

    this._listenForLastRealGame = true;
  }

  async setLastPlayedRealGame(gameType: number): Promise<void> {
    const lastGameInfo = await this.services.games.getGameInfo(gameType);
    const correlatedGames = await this.services.games.getCorrelatedGames(gameType, 50);
    let gameInfosArray = (await this.services.games.getGamesInfoArray(correlatedGames)) || [];

    this._setLastPlayedRealGame(lastGameInfo);
    this._setCorrelatedGamesForLastPlayedRealGame(gameInfosArray);
  }

  private _listenForLastOpenDemoGame = false;

  @observable.ref
  lastOpenDemoGame: GameInfo = null;

  listenForLastOpenDemoGame() {
    if (this._listenForLastOpenDemoGame) {
      return;
    }

    this.services.sdk.events.onGameOpen((options: OnGameOpenParams) => {
      if (!options.isReal) {
        this.setLastOpenDemoGame(options.gameType);
      }
    });

    this._listenForLastOpenDemoGame = true;
  }

  async setLastOpenDemoGame(gameType: number): Promise<void> {
    const gameInfo = await this.services.games.getGameInfo(gameType);
    this._setLastOpenDemoGame(gameInfo);
  }

  @action.bound
  _setLastOpenDemoGame(lastGameInfo: GameInfo) {
    this.lastOpenDemoGame = lastGameInfo;
  }
}
